import React, {
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { RuleAttributes, RuleType } from '@loyalty/shared-common/rule-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useProgram } from '../loyalty/ProgramContext';

interface RulesContextInterface {
  earningRules: RuleAttributes[];
  setEarningRules: React.Dispatch<SetStateAction<RuleAttributes[]>>;
  burningRules: RuleAttributes[];
  setBurningRules: React.Dispatch<SetStateAction<RuleAttributes[]>>;
  accessRule: RuleAttributes | undefined;
  setAccessRule: (accessRule: RuleAttributes | undefined) => void;
  selectedRule: RuleAttributes | undefined;
  fetchRules: (type: RuleType) => void;
  setSelectedRule: (rule: RuleAttributes) => void;
  isLoading: IsLoading;
}

export const RulesContext = createContext<RulesContextInterface>({} as any);

export function useRules() {
  return useContext(RulesContext);
}

export function RulesProvider({ children }: { children: ReactNode }) {
  const { program } = useProgram();
  const { notify } = useAlerts();
  const { t } = useTranslation();

  const { ruleApi } = useApi();

  const [earningRules, setEarningRules] = useState<RuleAttributes[]>([]);
  const [burningRules, setBurningRules] = useState<RuleAttributes[]>([]);
  const [accessRule, setAccessRule] = useState<RuleAttributes>();
  const [selectedRule, setSelectedRule] = useState<RuleAttributes>();

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const fetchRules = useCallback(
    (type?: RuleType) => {
      addLoader('fetchRules');

      ruleApi
        .getRules(program.id, { type })
        .then(rules => {
          switch (type) {
            case RuleType.ACCESS:
              setAccessRule(rules[0]);
              return;
            case RuleType.EARN:
              setEarningRules(rules);
              return;
            case RuleType.BURN:
              setBurningRules(rules);
              return;
            default:
              setEarningRules(rules.filter(rule => rule.type === RuleType.EARN));
              setBurningRules(rules.filter(rule => rule.type === RuleType.BURN));
              setAccessRule(rules.find(rule => rule.type === RuleType.ACCESS));
          }
        })
        .catch(error => {
          switch (type) {
            case RuleType.ACCESS:
              setAccessRule(undefined);
              notify(t('access-rule.errors.failed-fetch-rules', { error }), 'error');
              return;
            case RuleType.EARN:
              setEarningRules([]);
              notify(t('earning-rules.errors.failed-fetch-rules', { error }), 'error');
              return;
            case RuleType.BURN:
              setBurningRules([]);
              notify(t('burning-rules.errors.failed-fetch-rules', { error }), 'error');
              return;
            default:
              setEarningRules([]);
              setBurningRules([]);
              setAccessRule(undefined);
              notify(t('rules.errors.failed-fetch-rules', { error }), 'error');
              return;
          }
        })
        .finally(() => removeLoader('fetchRules'));
    },
    [addLoader, notify, program.id, removeLoader, ruleApi, t],
  );

  useEffect(() => {
    fetchRules();
  }, [program.id, fetchRules]);

  const context: RulesContextInterface = {
    earningRules,
    setEarningRules,
    burningRules,
    setBurningRules,
    accessRule,
    setAccessRule,
    selectedRule,
    fetchRules,
    setSelectedRule,
    isLoading,
  };

  return <RulesContext.Provider value={context}>{children}</RulesContext.Provider>;
}
